import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  getDebtorAveragePaymentDays,
  getEffectiveMaxDate,
} from '../../helpers/date-to-pay-restrictions';
import {
  DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
  daysFromTodayToDate,
} from '../../constants';
import useGetCountryFromUrl from '../../hooks/useGetCountryFromUrl';
import { useCartDateDisableCheck } from '../../hooks/useUpcomingHolidays';
import OnboardingDatePicker from '../datePickers/OnboardingDatePicker';
import GridDateButton from '../buttons/GridDateButton';

const DateToPaySelectCell = ({
  row,
  selectedDaysButtonsOnChange,
  selectedDaysButtonsOnRecommendedPaymentTerm,
  sameDateForAllSelection,
  minDaysFromToday,
}) => {
  const [dateValue, setDateValue] = useState(null);
  const country = useGetCountryFromUrl();
  const shouldDisableDate = useCartDateDisableCheck();

  const debtorAveragePaymentDays = useMemo(
    () => getDebtorAveragePaymentDays(
      row?.invoicesActualPaymentTerm,
      row?.dateIssued,
    ),
    [row],
  );

  useEffect(() => {
    if (sameDateForAllSelection) {
      setDateValue(sameDateForAllSelection);
    }
  }, [sameDateForAllSelection]);

  const maxDate = useMemo(() => getEffectiveMaxDate(row, country), [row, country]);

  const pickerMinDate = useMemo(
    () => daysFromTodayToDate(minDaysFromToday),
    [minDaysFromToday],
  );

  const datePickerOnChange = (newValue) => {
    setDateValue(newValue);
    selectedDaysButtonsOnChange(newValue, row);
  };

  const GridDateButtonCallback = useCallback(({
    setOpen,
    value: buttonValue,
    inputProps,
    error,
    inputRef,
    InputProps,
    ...buttonParams
  }) => (
    <GridDateButton
      setOpen={setOpen}
      value={buttonValue}
      {...buttonParams}
    />
  ), []);

  return (
    <OnboardingDatePicker
      value={dateValue}
      onChange={datePickerOnChange}
      debtorAveragePaymentDays={debtorAveragePaymentDays}
      minDate={pickerMinDate}
      maxDate={maxDate}
      shouldDisableWeekend={shouldDisableDate}
      ButtonComponent={GridDateButtonCallback}
      selectedDaysButtonsOnRecommendedPaymentTerm={
        selectedDaysButtonsOnRecommendedPaymentTerm
      }
    />
  );
};

DateToPaySelectCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    invoicesActualPaymentTerm: PropTypes.number,
    dateIssued: PropTypes.shape({
    }),
  }).isRequired,
  selectedDaysButtonsOnChange: PropTypes.func.isRequired,
  selectedDaysButtonsOnRecommendedPaymentTerm: PropTypes.func.isRequired,
  sameDateForAllSelection: PropTypes.instanceOf(dayjs),
  minDaysFromToday: PropTypes.number,
};

DateToPaySelectCell.defaultProps = {
  sameDateForAllSelection: null,
  minDaysFromToday: DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
};

export default DateToPaySelectCell;
